import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { breadcrumbSchema, faqSchema } from '../utils/schemas';
import {
  Shield,
  FileSignature,
  Database,
  Calendar,
  Lock,
  Globe,
  ExternalLink,
  Server,
  ChevronDown,
} from 'lucide-react';

const Technology = () => {
  const ecosystemProducts = [
    {
      icon: FileSignature,
      name: 'SignAndGo',
      url: 'https://signandgo.com.au',
      description:
        "All loan documents signed electronically with Australia's eSignature platform.",
    },
    {
      icon: Database,
      name: 'CRMandGo',
      url: 'https://crmandgo.com.au',
      description:
        'Your application tracked from enquiry to settlement. Purpose-built CRM for mortgage brokers.',
    },
    {
      icon: Shield,
      name: 'ShareAndGo',
      url: 'https://shareandgo.com.au',
      description:
        'Supporting documents shared via secure data rooms with bank-grade encryption.',
    },
    {
      icon: Calendar,
      name: 'BookAndGo',
      url: 'https://bookandgo.com.au',
      description:
        "Book your consultation online. Seamless scheduling integrated with your broker's calendar.",
    },
  ];

  const faqs = [
    {
      question: 'Is my data stored in Australia?',
      answer:
        'Not all of it, and we tell you exactly what goes where. The details you send us in an enquiry (your name, email address, phone number, loan details and message) are stored in Google Cloud in the United States. The service that receives your enquiry runs in Google Cloud\'s Sydney region. Emails and text messages are sent by Twilio SendGrid and Twilio in the United States, and our website uses Google reCAPTCHA and Google Analytics. Every service, and where it runs, is listed on this page.',
    },
    {
      question: 'How do I sign loan documents electronically?',
      answer:
        'We use SignAndGo, an Australian-built eSignature platform, to send your loan documents for signing. You will receive a secure link via email, review the documents in your browser, and sign electronically from any device. All signatures are legally binding under Australian law.',
    },
    {
      question: 'Can I track my loan application progress?',
      answer:
        'Absolutely. Our CRM platform, CRMandGo, tracks your application from initial enquiry through to settlement. Your broker can provide status updates at any time, and you will receive notifications at key milestones throughout the process.',
    },
    {
      question: 'Is the technology secure?',
      answer:
        'Security is a core pillar of the AndGo ecosystem. All platforms use bank-grade encryption (AES-256), secure HTTPS connections, and multi-factor authentication.',
    },
  ];

  const storageRows = [
    { what: 'Stores your enquiry', provider: 'Google Cloud', where: 'United States', handles: 'Your name, email address, phone number, loan details and message' },
    { what: 'Receives your enquiry', provider: 'Google Cloud', where: 'Sydney, Australia', handles: 'Your enquiry as it arrives' },
    { what: 'Manages your enquiry', provider: 'WorkAndGo', where: 'Sydney, Australia', handles: 'Your enquiry details' },
    { what: 'Tracks your application', provider: 'CRMandGo', where: 'Sydney, Australia', handles: 'Your enquiry details' },
    { what: 'Email', provider: 'Twilio SendGrid', where: 'United States', handles: 'Email addresses and message content' },
    { what: 'Text messages', provider: 'Twilio', where: 'United States', handles: 'Mobile numbers and message content' },
    { what: 'Spam protection', provider: 'Google reCAPTCHA', where: "Google's global data centres", handles: 'How the enquiry form is used, and your IP address' },
    { what: 'Website analytics', provider: 'Google Analytics', where: "Google's global data centres", handles: 'How our website is used' },
    { what: 'Website delivery', provider: 'Google Firebase Hosting', where: "Google's global network", handles: 'Connection details such as your IP address' },
    { what: 'Visitor counts', provider: 'ManageYourTech', where: 'Sydney, Australia', handles: 'Page visits, and connection details such as your IP address' },
  ];

  const schema = [
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Technology', url: '/technology' },
    ]),
    faqSchema(faqs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Technology"
        description="Discover the Australian-built AndGo ecosystem that powers Manage Your Loans. Secure document signing, CRM tracking, file sharing, and online booking, plus every service that handles your enquiry and where it runs."
        path="/technology"
        schema={schema}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#00594C] to-[#003d35] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <Server className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Powered by Australian Technology
            </h1>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto">
              Manage Your Loans is built on the AndGo ecosystem — a suite of integrated,
              Australian-built platforms that deliver a seamless loan experience from first enquiry
              to settlement.
            </p>
          </div>
        </div>
      </section>

      {/* Ecosystem Products */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">The AndGo Ecosystem</h2>
              <p className="text-gray-600 text-lg">
                Four purpose-built platforms working together to streamline your loan journey
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {ecosystemProducts.map((product) => (
                <a
                  key={product.name}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300 group block"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#99DF63]/20 rounded-lg flex items-center justify-center">
                      <product.icon className="w-6 h-6 text-[#00594C]" />
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#00594C] transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#00594C] mb-2">{product.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MyAndGo Platform Hub */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#99DF63]/20 rounded-full mb-6">
              <Globe className="w-7 h-7 text-[#00594C]" />
            </div>
            <h2 className="text-3xl font-bold text-[#00594C] mb-4">MyAndGo — The Platform Hub</h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              All AndGo products connect through{' '}
              <a
                href="https://myandgo.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00594C] font-semibold underline hover:text-[#003d35]"
              >
                MyAndGo
              </a>
              , a unified platform that brings together document signing, application tracking,
              secure file sharing, and appointment booking into a single experience. Whether you are
              a broker or a borrower, MyAndGo is your central hub for everything loan-related.
            </p>
          </div>
        </div>
      </section>

      {/* Where your information is stored */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-[#00594C] to-[#003d35] rounded-2xl p-8 md:p-12 text-white">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mr-4">
                  <Lock className="w-6 h-6 text-[#99DF63]" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Where your information is stored</h2>
              </div>
              <p className="text-gray-200 text-lg leading-relaxed mb-6">
                Some services that handle your enquiry are provided by specialist companies, and
                some of them operate outside Australia. We name every one of them, and where it
                runs.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-200">
                  <thead>
                    <tr className="border-b border-white/20 text-white">
                      <th scope="col" className="py-2 pr-4 font-semibold">What it does</th>
                      <th scope="col" className="py-2 pr-4 font-semibold">Provider</th>
                      <th scope="col" className="py-2 pr-4 font-semibold">Where it runs</th>
                      <th scope="col" className="py-2 font-semibold">What it handles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storageRows.map((row) => (
                      <tr key={row.what + row.provider} className="border-b border-white/10 align-top">
                        <td className="py-2 pr-4">{row.what}</td>
                        <td className="py-2 pr-4">{row.provider}</td>
                        <td className="py-2 pr-4">{row.where}</td>
                        <td className="py-2">{row.handles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-gray-200 leading-relaxed mt-6">
                We don&apos;t sell your personal information. Our{' '}
                <a href="/privacy-policy" className="underline text-white hover:text-[#99DF63]">
                  privacy policy
                </a>{' '}
                explains how we handle it under the Australian Privacy Principles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#00594C] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 text-lg">Common questions about our technology</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl border border-gray-100 group"
                >
                  <summary className="flex items-center justify-between cursor-pointer p-6 list-none">
                    <h3 className="text-lg font-semibold text-[#00594C] pr-4">{faq.question}</h3>
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Technology;
